/**
 * AikoProvider — the anti-intrusion contract layer.
 *
 * The full contract (rules, channel-naming, anti-patterns, open questions)
 * lives at `docs/contracts/aiko-intrusion.md`. This file is the runtime
 * enforcement; the doc is the spec. **Read the doc before extending this
 * provider or adding a new Aiko surface.**
 *
 * One-line summary: dismiss in one click, snooze 30 min after dismiss, three
 * dismisses in a session → silent for the session, mute per-surface persists
 * in localStorage, surfaces never block the canvas.
 *
 * Wrap once near the app root:
 *
 *   <AikoProvider>
 *     <App />
 *   </AikoProvider>
 *
 * Then any AikoTip with `channelId` becomes managed:
 *
 *   <AikoTip channelId="expert.grounding-strictness" variant="rail" ...>
 *     <Button>?</Button>
 *   </AikoTip>
 *
 * Or use the hook directly for advanced surfaces:
 *
 *   const { canShow, dismiss, mute } = useAikoChannel("some.channel");
 *
 * Without a provider, AikoTip with channelId still works (it just runs
 * unmanaged — no snooze tracking, no mute persistence). The provider is the
 * gate to actually using the contract.
 *
 * Persistence: snoozedUntil + muted persist via `useLocalStorage` keyed by
 * `storageKey` (default `aisquare-pretty-aiko-state`). Session dismiss count
 * lives in memory only — resets per page load.
 */
import * as React from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useAikoTarget } from "../hooks/useAikoTarget";
import { AikoWhisper } from "./AikoSurfaces";

/* ============================================================================
 * Constants
 * ========================================================================= */

/** Default snooze window after a dismiss. Per the Phase 3 contract: 30 min. */
const DEFAULT_SNOOZE_MS = 30 * 60 * 1000;

/** Default session-silence threshold. Per the contract: 3 dismisses in a session. */
const DEFAULT_SESSION_DISMISS_THRESHOLD = 3;

/** localStorage key for persisted channel state. */
const DEFAULT_STORAGE_KEY = "aisquare-pretty-aiko-state";

/** Default bound on how long a hint waits for its target to (re-)appear. */
const DEFAULT_HINT_QUEUE_TIMEOUT_MS = 30 * 1000;

/* ============================================================================
 * Types
 * ========================================================================= */

interface AikoChannelState {
  /** Unix ms after which the channel is allowed to show again. */
  snoozedUntil?: number;
  /** When true, the user has explicitly silenced this channel; persists until unmuted. */
  muted?: boolean;
}

type AikoStorageState = Record<string, AikoChannelState>;

interface AikoChannelDispatch {
  /** Standard dismiss: bumps the session count and sets snoozedUntil = now + defaultSnoozeDuration. */
  dismiss: () => void;
  /** Explicit snooze for a specific duration. */
  snoozeFor: (ms: number) => void;
  /** Permanently mute this channel until the user unmutes. */
  mute: () => void;
  unmute: () => void;
  /** Clear snooze + mute + session-dismiss state for this channel. */
  reset: () => void;
}

export interface AikoChannelHandle extends AikoChannelDispatch {
  /** Computed: true when the channel is allowed to render. */
  canShow: boolean;
  /** Reasons canShow may be false (useful for debug + UX hints). */
  isMuted: boolean;
  isSnoozed: boolean;
  isSessionSilenced: boolean;
  /** Unix ms when the snooze ends. `null` if not snoozed. */
  snoozedUntil: number | null;
  /** Number of dismisses in the current session. */
  sessionDismissCount: number;
}

/**
 * Escalation level a channel can be at. The detector (`useAikoSignal`)
 * upgrades the level in response to user signals; the surfaces
 * (`<AikoWhisper>`, `<AikoNudge>`, `<AikoIntervene>`) read the level to
 * decide whether to render.
 *
 * Per the Phase 3 design: silent → whisper → nudge → intervene. Levels are
 * monotonically escalating in normal use; only `dismiss()` / `resetLevel()`
 * lowers them (and dismiss applies the channel snooze).
 */
export type AikoLevel = "silent" | "whisper" | "nudge" | "intervene";

/* ---------------------------------------------------------------- Hints */

/**
 * Imperative hint API. Aiko's reasoning code calls `aiko.point({...})` to
 * highlight a UI target with a breathing whisper pip. The provider's
 * resolver looks the target up via `data-aiko-anchor` (see `<AikoAnchor>`),
 * decides whether to render based on visibility and the channel's
 * intrusion contract, and mounts an `<AikoWhisper>` against the resolved
 * element.
 *
 * MVP scopes to the `whisper` level. Richer surfaces (nudge / intervene)
 * with title/body/actions land in a follow-up — they need a different
 * config shape than the lightweight `{ reason }` payload here.
 */
export interface AikoPointOptions {
  /**
   * Anchor id matching `<AikoAnchor id>` (or a raw `data-aiko-anchor`
   * attribute) somewhere in the DOM. Namespace by feature
   * (`wizard.cover-button`, `studio.publish-cta`).
   */
  target: string;
  /**
   * Why Aiko is pointing — surfaced as the whisper's hover-preview text.
   * Keep it terse and sentence-cased; the whisper is a teaser, not a
   * full explanation.
   */
  reason: string;
  /**
   * Channel id for the intrusion contract (snooze / mute / session-silence).
   * Required so every Aiko-driven hint plays nice with `useAikoChannel`.
   */
  channelId: string;
  /**
   * What to do when the target is missing or hidden:
   *   - `"queue"` (default) — wait for it to appear, bounded by `queueTimeoutMs`.
   *   - `"skip"`            — drop the hint immediately if unreachable.
   * Disabled targets always render: the hint sits next to the disabled
   * element and the `reason` becomes the explainer for why it's disabled.
   */
  onUnreachable?: "queue" | "skip";
  /** Bound on the queue wait. Default 30s — long enough for a route change, short enough to not ambush a different surface. */
  queueTimeoutMs?: number;
  /** Side of the anchor to place the pip. Default `"right"`. */
  side?: "top" | "right" | "bottom" | "left";
  /**
   * Stable id for de-duping. Same id → replace the prior hint. Omitting
   * generates a unique id, so back-to-back calls stack instead of replacing.
   */
  id?: string;
}

export interface AikoPointHandle {
  /** The hint id (auto-generated when not supplied to `point`). */
  id: string;
  /** Remove the hint. Does NOT count as a user dismiss for the channel. */
  dismiss: () => void;
}

interface PendingHint extends Required<Omit<AikoPointOptions, "id">> {
  id: string;
}

export interface AikoContextValue {
  /** All persisted channel state. */
  channels: AikoStorageState;
  /** Default snooze duration (ms) when a channel calls `dismiss()`. */
  defaultSnoozeDuration: number;
  /** Number of session dismisses after which a channel falls silent for the session. */
  sessionDismissThreshold: number;
  /** Per-channel session dismiss counts (in-memory). */
  sessionDismisses: Record<string, number>;
  /** Per-channel escalation levels (in-memory; resets per page load). */
  levels: Record<string, AikoLevel>;
  /** Active hints registered via `point()`, keyed by hint id. */
  hints: Record<string, PendingHint>;

  /* mutators */
  dismissChannel: (channelId: string, snoozeMs?: number) => void;
  snoozeChannel: (channelId: string, ms: number) => void;
  muteChannel: (channelId: string) => void;
  unmuteChannel: (channelId: string) => void;
  resetChannel: (channelId: string) => void;
  resetAll: () => void;
  setChannelLevel: (channelId: string, level: AikoLevel) => void;
  /** Imperative — fire a hint at a target. Returns a handle with `id` + `dismiss`. */
  point: (options: AikoPointOptions) => AikoPointHandle;
  /** Imperative — remove a hint by id without counting it as a user dismiss. */
  dismissHint: (hintId: string) => void;
}

const AikoContext = React.createContext<AikoContextValue | null>(null);

/* ============================================================================
 * Provider
 * ========================================================================= */

export interface AikoProviderProps {
  children: React.ReactNode;
  /** Override the default 30-minute snooze. */
  defaultSnoozeDuration?: number;
  /** Override the 3-dismiss session-silence threshold. */
  sessionDismissThreshold?: number;
  /** Override the localStorage key used for persistence. */
  storageKey?: string;
}

export function AikoProvider({
  children,
  defaultSnoozeDuration = DEFAULT_SNOOZE_MS,
  sessionDismissThreshold = DEFAULT_SESSION_DISMISS_THRESHOLD,
  storageKey = DEFAULT_STORAGE_KEY,
}: AikoProviderProps) {
  const [channels, setChannels] = useLocalStorage<AikoStorageState>(
    storageKey,
    {},
  );
  // Session dismisses live in memory only — resets on page load.
  const [sessionDismisses, setSessionDismisses] = React.useState<
    Record<string, number>
  >({});
  // Escalation levels are also in-memory only.
  const [levels, setLevels] = React.useState<Record<string, AikoLevel>>({});
  // Active imperative hints (from `aiko.point({...})`). In-memory only.
  const [hints, setHints] = React.useState<Record<string, PendingHint>>({});
  const hintCounterRef = React.useRef(0);

  const updateChannel = React.useCallback(
    (
      channelId: string,
      patch: Partial<AikoChannelState> | ((prev: AikoChannelState) => Partial<AikoChannelState>),
    ) => {
      setChannels((prev) => {
        const current = prev[channelId] ?? {};
        const next = typeof patch === "function" ? patch(current) : patch;
        const merged = { ...current, ...next };
        // Strip falsy/undefined fields so storage stays small.
        const cleaned: AikoChannelState = {};
        if (merged.snoozedUntil && merged.snoozedUntil > Date.now()) {
          cleaned.snoozedUntil = merged.snoozedUntil;
        }
        if (merged.muted) cleaned.muted = true;
        if (Object.keys(cleaned).length === 0) {
          // Drop the channel entry entirely
          const { [channelId]: _omit, ...rest } = prev;
          void _omit;
          return rest;
        }
        return { ...prev, [channelId]: cleaned };
      });
    },
    [setChannels],
  );

  const dismissChannel = React.useCallback(
    (channelId: string, snoozeMs?: number) => {
      const ms = snoozeMs ?? defaultSnoozeDuration;
      updateChannel(channelId, { snoozedUntil: Date.now() + ms });
      setSessionDismisses((prev) => ({
        ...prev,
        [channelId]: (prev[channelId] ?? 0) + 1,
      }));
      // Drop the level back to silent — surfaces stop rendering.
      setLevels((prev) => {
        if (!prev[channelId] || prev[channelId] === "silent") return prev;
        const { [channelId]: _omit, ...rest } = prev;
        void _omit;
        return rest;
      });
    },
    [defaultSnoozeDuration, updateChannel],
  );

  const snoozeChannel = React.useCallback(
    (channelId: string, ms: number) => {
      updateChannel(channelId, { snoozedUntil: Date.now() + ms });
    },
    [updateChannel],
  );

  const muteChannel = React.useCallback(
    (channelId: string) => {
      updateChannel(channelId, { muted: true });
    },
    [updateChannel],
  );

  const unmuteChannel = React.useCallback(
    (channelId: string) => {
      updateChannel(channelId, (prev) => ({ ...prev, muted: false }));
    },
    [updateChannel],
  );

  const resetChannel = React.useCallback(
    (channelId: string) => {
      setChannels((prev) => {
        const { [channelId]: _omit, ...rest } = prev;
        void _omit;
        return rest;
      });
      setSessionDismisses((prev) => {
        const { [channelId]: _omit, ...rest } = prev;
        void _omit;
        return rest;
      });
      setLevels((prev) => {
        const { [channelId]: _omit, ...rest } = prev;
        void _omit;
        return rest;
      });
    },
    [setChannels],
  );

  const resetAll = React.useCallback(() => {
    setChannels({});
    setSessionDismisses({});
    setLevels({});
  }, [setChannels]);

  const setChannelLevel = React.useCallback(
    (channelId: string, level: AikoLevel) => {
      setLevels((prev) => {
        if (level === "silent") {
          const { [channelId]: _omit, ...rest } = prev;
          void _omit;
          return rest;
        }
        if (prev[channelId] === level) return prev;
        return { ...prev, [channelId]: level };
      });
    },
    [],
  );

  const dismissHint = React.useCallback((hintId: string) => {
    setHints((prev) => {
      if (!(hintId in prev)) return prev;
      const { [hintId]: _omit, ...rest } = prev;
      void _omit;
      return rest;
    });
  }, []);

  const point = React.useCallback(
    (options: AikoPointOptions): AikoPointHandle => {
      const id =
        options.id ??
        `aiko-hint-${++hintCounterRef.current}-${Date.now().toString(36)}`;
      const hint: PendingHint = {
        id,
        target: options.target,
        reason: options.reason,
        channelId: options.channelId,
        onUnreachable: options.onUnreachable ?? "queue",
        queueTimeoutMs: options.queueTimeoutMs ?? DEFAULT_HINT_QUEUE_TIMEOUT_MS,
        side: options.side ?? "right",
      };
      setHints((prev) => ({ ...prev, [id]: hint }));
      return { id, dismiss: () => dismissHint(id) };
    },
    [dismissHint],
  );

  const value = React.useMemo<AikoContextValue>(
    () => ({
      channels,
      defaultSnoozeDuration,
      sessionDismissThreshold,
      sessionDismisses,
      levels,
      hints,
      dismissChannel,
      snoozeChannel,
      muteChannel,
      unmuteChannel,
      resetChannel,
      resetAll,
      setChannelLevel,
      point,
      dismissHint,
    }),
    [
      channels,
      defaultSnoozeDuration,
      sessionDismissThreshold,
      sessionDismisses,
      levels,
      hints,
      dismissChannel,
      snoozeChannel,
      muteChannel,
      unmuteChannel,
      resetChannel,
      resetAll,
      setChannelLevel,
      point,
      dismissHint,
    ],
  );

  return (
    <AikoContext.Provider value={value}>
      {children}
      {Object.values(hints).map((hint) => (
        <ActiveHint key={hint.id} hint={hint} onUnregister={dismissHint} />
      ))}
    </AikoContext.Provider>
  );
}

AikoProvider.displayName = "AikoProvider";

/* ============================================================================
 * Hooks
 * ========================================================================= */

/**
 * Read the global Aiko context. Throws if used outside `<AikoProvider>`.
 *
 * For most use cases, prefer `useAikoChannel(channelId)` — it returns a tidy
 * per-channel handle with `canShow` / `dismiss` / `mute` baked in.
 */
export function useAiko(): AikoContextValue {
  const ctx = React.useContext(AikoContext);
  if (!ctx) {
    throw new Error(
      "useAiko must be used inside <AikoProvider>. Wrap your app root.",
    );
  }
  return ctx;
}

/**
 * Per-channel handle. Returns sensible defaults outside a provider so consumers
 * can adopt the contract incrementally — Aiko surfaces with `channelId` work
 * either way (managed when wrapped, unmanaged otherwise).
 *
 *   const { canShow, dismiss, mute, isMuted } = useAikoChannel("some.channel");
 *   if (!canShow) return null;
 */
export function useAikoChannel(channelId: string): AikoChannelHandle {
  const ctx = React.useContext(AikoContext);

  // Re-evaluate canShow when wall-clock time crosses snoozedUntil. Without
  // this, a snoozed channel would stay hidden until the next render — fine
  // for most cases, but a long-mounted detector would never update on its
  // own. Schedule a single timeout to re-render when the snooze expires.
  const [, force] = React.useReducer((n: number) => n + 1, 0);
  const snoozedUntil = ctx?.channels[channelId]?.snoozedUntil ?? null;
  React.useEffect(() => {
    if (!snoozedUntil) return;
    const remaining = snoozedUntil - Date.now();
    if (remaining <= 0) return;
    const id = window.setTimeout(force, remaining + 50);
    return () => window.clearTimeout(id);
  }, [snoozedUntil]);

  // Outside a provider — graceful no-op handle.
  if (!ctx) {
    return {
      canShow: true,
      isMuted: false,
      isSnoozed: false,
      isSessionSilenced: false,
      snoozedUntil: null,
      sessionDismissCount: 0,
      dismiss: noop,
      snoozeFor: noop,
      mute: noop,
      unmute: noop,
      reset: noop,
    };
  }

  const channel = ctx.channels[channelId] ?? {};
  const sessionDismissCount = ctx.sessionDismisses[channelId] ?? 0;
  const isMuted = !!channel.muted;
  const isSnoozed = !!channel.snoozedUntil && channel.snoozedUntil > Date.now();
  const isSessionSilenced =
    sessionDismissCount >= ctx.sessionDismissThreshold;
  const canShow = !isMuted && !isSnoozed && !isSessionSilenced;

  return {
    canShow,
    isMuted,
    isSnoozed,
    isSessionSilenced,
    snoozedUntil: isSnoozed ? channel.snoozedUntil ?? null : null,
    sessionDismissCount,

    dismiss: () => ctx.dismissChannel(channelId),
    snoozeFor: (ms: number) => ctx.snoozeChannel(channelId, ms),
    mute: () => ctx.muteChannel(channelId),
    unmute: () => ctx.unmuteChannel(channelId),
    reset: () => ctx.resetChannel(channelId),
  };
}

const noop = () => {};

/* ============================================================================
 * Hint resolver — renders one <AikoWhisper> per pending hint.
 *
 * Each ActiveHint independently:
 *   - looks up its target via `useAikoTarget(id)` (re-renders on DOM changes)
 *   - reads the channel state via `useAikoChannel(channelId)` so the hint
 *     respects snooze / mute / session-silence the same way every other
 *     Aiko surface does
 *   - applies the `onUnreachable` policy when the target is missing or hidden
 *     (queue with bounded wait, or skip immediately)
 *   - mounts <AikoWhisper> against the resolved element when everything lines
 *     up; unmounts when the target re-hides or the channel goes silent (the
 *     hint stays in the registry so it can re-surface later if conditions
 *     change before the queue timeout fires)
 * ========================================================================= */

function ActiveHint({
  hint,
  onUnregister,
}: {
  hint: PendingHint;
  onUnregister: (hintId: string) => void;
}) {
  const target = useAikoTarget(hint.target);
  // Render the whisper against a synthetic ref so AikoWhisper's
  // useVirtualAnchor measures the latest element on every frame.
  const anchorRef = React.useMemo<React.RefObject<HTMLElement | null>>(
    () => ({ current: target.element }),
    [target.element],
  );

  // Bounded queue / skip policy when the target isn't usable. Disabled
  // targets count as usable — they're visible, the whisper sits beside
  // them and `reason` becomes the explainer.
  const reachable = target.exists && target.visible;
  React.useEffect(() => {
    if (reachable) return;
    if (hint.onUnreachable === "skip") {
      // Defer the unregister so we don't update state during render commit.
      const id = window.setTimeout(() => onUnregister(hint.id), 0);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => onUnregister(hint.id), hint.queueTimeoutMs);
    return () => window.clearTimeout(id);
  }, [reachable, hint.onUnreachable, hint.queueTimeoutMs, hint.id, onUnregister]);

  if (!reachable) return null;

  return (
    <AikoWhisper
      anchorRef={anchorRef}
      open={true}
      channelId={hint.channelId}
      previewText={hint.reason}
      previewSide={hint.side}
      side={hint.side}
      onDismiss={() => onUnregister(hint.id)}
    />
  );
}
